import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingPendoHelmet from 'common/helmets/LandingPendoHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LandingLogos from 'common/LandingLogos';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import PendoLogo from 'img/landing/pendo-logo.webp';
import Subdomain1x from 'img/landing/subdomain-1x.webp';
import Subdomain2x from 'img/landing/subdomain-2x.webp';

import CannyLogo from 'img/canny-wordmark-white.svg';

import 'css/components/www/_LandingComparisonPageNew.scss';

const LandingPendo = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Pendo Page');
  }, [trackEvent]);

  return (
    <div className="LandingPendo landingComparisonPage">
      <LandingPendoHelmet />
      <div className="mainContentContainer">
        <div className="header">
          <div className="left">
            <h1>Canny is the feedback-driven Pendo&nbsp;Alternative</h1>
            <p className="text">
              Pendo is a product analytics tool. Their feedback management tool is an afterthought,
              and it&nbsp;shows.
            </p>
            <p className="text">
              Canny is a powerful, yet easy-to-use, product management software built around user
              feedback. It's easy to set up, cost-effective, and makes feedback management
              a&nbsp;breeze.
            </p>
          </div>
          <LazyLoadedImage
            className="headerImg"
            loading="eager"
            src={Subdomain1x}
            srcSet={`${Subdomain1x} 1x, ${Subdomain2x} 2x`}
            alt="A feedback board in Canny, a Pendo alternative, displaying users’ feature requests"
          />
        </div>
        <div className="section">
          <h2 className="sectionHeader">Table of contents</h2>
          <div className="sectionContent">
            <a href="#quick-look" className="text toc-option">
              <p className="number">01</p>
              <p className="option-text">Quick look: why people prefer Canny over&nbsp;Pendo</p>
            </a>
            <a href="#compared" className="text toc-option">
              <p className="number">02</p>
              <p className="option-text">Canny compared to Pendo</p>
            </a>
            <a href="#user-experience" className="text toc-option">
              <p className="number">03</p>
              <p className="option-text">Canny offers a more intuitive user&nbsp;experience</p>
            </a>
            <a href="#pricing" className="text toc-option">
              <p className="number">04</p>
              <p className="option-text">Canny is less expensive than&nbsp;Pendo</p>
            </a>
            <a href="#customers" className="text toc-option">
              <p className="number">05</p>
              <p className="option-text">Canny customers and use&nbsp;cases</p>
            </a>
            <a href="#migration" className="text toc-option">
              <p className="number">06</p>
              <p className="option-text">Switching to Canny from&nbsp;Pendo</p>
            </a>
          </div>
        </div>
        <div className="section" id="quick-look">
          <h2 className="sectionHeader">Quick look: why people prefer Canny over&nbsp;Pendo</h2>
          <div className="sectionContent">
            <div className="comparisonChart">
              <div className="comparisonLogoHeader">
                <div className="canny">
                  <LazyLoadedImage alt="Canny's logo" src={CannyLogo} />
                </div>
                <div className="competitorLogo pendo">
                  <LazyLoadedImage alt="Pendo's logo" src={PendoLogo} />
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <h4 className="title">Feedback first</h4>
                  <p className="description">
                    The best tools for collecting, managing, and analyzing user&nbsp;feedback.
                  </p>
                </div>
                <div className="point">
                  <h4 className="title">Feedback… kinda?</h4>
                  <p className="description">
                    Undeveloped feedback tools with little&nbsp;flexibility
                  </p>
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <h4 className="title">Only the features you need</h4>
                  <p className="description">
                    We integrate with your existing workflow and&nbsp;tools
                  </p>
                </div>
                <div className="point">
                  <h4 className="title">Feature bloated</h4>
                  <p className="description">
                    Lots of tools available, but few are best-in&nbsp;class
                  </p>
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <h4 className="title">Simple, straightforward design</h4>
                  <p className="description">Intuitive user experience and easy to&nbsp;use</p>
                </div>
                <div className="point">
                  <h4 className="title">Clunky user interface</h4>
                  <p className="description">
                    There's too much going on and it can be confusing to&nbsp;use
                  </p>
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <h4 className="title">Committed to customer success</h4>
                  <p className="description">
                    Expect top-notch customer support regardless of what plan you're&nbsp;on
                  </p>
                </div>
                <div className="point">
                  <h4 className="title">Enterprise only</h4>
                  <p className="description">
                    Pendo focuses on enterprise accounts. Getting help can be challenging if you're
                    not a huge&nbsp;company.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="section" id="compared">
          <h2 className="sectionHeader">Canny compared to&nbsp; Pendo</h2>
          <div className="sectionContent">
            <p className="text">
              Pendo is primarily a product experience and digital adoption platform. They also offer
              Pendo Analytics, user onboarding, and roadmapping&nbsp;tools.
            </p>
            <p className="text">
              But, we're looking at product feedback management. Canny is the strongest Pendo
              alternative in that&nbsp;area.
            </p>
            <p className="text">
              Canny gives you everything you need to close the feedback&nbsp;loop:
            </p>
            <ul className="comparisonList">
              <li>Capture, manage, and analyze feedback from all&nbsp;sources</li>
              <li>
                Add users' feature requests to your roadmap and keep them updated on
                your&nbsp;progress
              </li>
              <li>Publish release notes to let users know you acted on their feedback</li>
            </ul>
            <p className="text">
              Feedback is far from Pendo's core focus. They acquired a feedback tool called
              Receptive in 2019, and that became Pendo Feedback. It hasn’t changed much
              since&nbsp;then.
            </p>
            <p className="text">
              The feature is undeveloped and doesn't work well with their other tools. They say as
              much on their own&nbsp;website:
            </p>
            <div className="quoteContainer">
              <p className="quote">
                "Does Pendo Feedback integrate with Pendo Analytics and Pendo In-app Guides? We have
                a lightweight integration between these products today, and are actively working on
                rebuilding Feedback inside the rest of the Pendo&nbsp;platform."
              </p>
            </div>
            <p className="text">
              Their customers also voice that Pendo Feedback feels&nbsp;incomplete:
            </p>
            <div className="quoteContainer">
              <p className="quote">
                Currently, we are using the feedback product, which is not mature, and should have
                been in Beta&nbsp;mode
              </p>
              <div className="credit">
                <p className="text">
                  <p className="title">
                    –{' '}
                    <a
                      href="https://www.g2.com/survey_responses/pendo-review-6790408"
                      rel="noopener nofollow"
                      target="_blank">
                      G2 Review of Pendo
                    </a>
                  </p>
                </p>
              </div>
            </div>
            <p className="text">
              There's a big functionality gap between Canny and Pendo&nbsp;Feedback.
            </p>
            <div className="comparisonChart">
              <div className="comparisonLogoHeader">
                <div className="canny">
                  <LazyLoadedImage alt="Canny's logo" src={CannyLogo} />
                </div>
                <div className="competitorLogo pendo">
                  <LazyLoadedImage alt="Pendo's logo" src={PendoLogo} />
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <p className="description">Tools to help automate feedback&nbsp;management</p>
                </div>
                <div className="point">
                  <p className="description">Have to manually review each piece of&nbsp;feedback</p>
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <p className="description">
                    <a href="/integrations" target="_blank" rel="noopener">
                      Integrates
                    </a>{' '}
                    with a wide range of tools: customer success, CRM, project management,
                    communication, and&nbsp;others
                  </p>
                </div>
                <div className="point">
                  <p className="description">
                    Missing important integrations with many customer success, CRM, and project
                    management&nbsp;tools
                  </p>
                </div>
              </div>
              <div className="comparison">
                <div className="point">
                  <p className="description">
                    Feedback = Canny’s core – available on all plans (including&nbsp;Free)
                  </p>
                </div>
                <div className="point">
                  <p className="description">
                    Feedback only available as an add-on to paid Pendo&nbsp;plans
                  </p>
                </div>
              </div>
            </div>
            <p className="text">
              Canny is a product management tool built around the idea that user feedback is vital.
              It’s not an afterthought; it should inform your roadmap and product&nbsp;decisions.
            </p>
            <div className="quoteContainer">
              <p className="quote">
                <p className="text">
                  “Canny really helps us not just with feedback management, but also with
                  decision-making. It pushes us to be more creative with what we’re building next.
                  Because, Canny helps us balance user ideas with our own&nbsp;input.”
                </p>
              </p>
              <div className="credit">
                <p className="text">
                  <div className="credit">
                    <p className="text">
                      <p className="name">Philippe Lehoux</p>
                      <p className="title">
                        CEO at{' '}
                        <a href="/case-studies/missive" rel="noopener" target="_blank">
                          Missive
                        </a>
                      </p>
                    </p>
                  </div>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="section" id="user-experience">
          <h2 className="sectionHeader">Canny offers a more intuitive user&nbsp;experience</h2>
          <div className="sectionContent">
            <p className="text">
              Canny is straightforward and intuitive to use. It's easy to set up, customize, and not
              unnecessarily&nbsp;complicated.
            </p>
            <p className="text">
              Pendo’s interface has too much going on and it can be difficult to&nbsp;use.
            </p>
            <div className="quoteContainer">
              <p className="quote">
                “The interface is tedious, and it is hard to find things if you are not entirely
                familiar with the&nbsp;platform.”
              </p>
              <div className="credit">
                <p className="text">
                  <p className="title">
                    –{' '}
                    <a
                      href="https://www.g2.com/products/pendo-io-pendo/reviews/pendo-review-7472727"
                      rel="noopener nofollow"
                      target="_blank">
                      G2 Review
                    </a>
                  </p>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="section" id="pricing">
          <h2 className="sectionHeader">Canny is less expensive than&nbsp;Pendo</h2>
          <div className="sectionContent">
            <p className="text">
              It's hard to know how much you'll pay to access Pendo Feedback. It's only available as
              an add-on to their paid plans which start at $7,000 per year. You'll need to speak to
              sales and get a quote, but expect to pay between $14,000 to $50,000 for a plan
              including&nbsp;feedback.
            </p>
            <p className="text">
              They also charge based on monthly active users, so costs will increase as
              you&nbsp;grow.
            </p>

            <p className="text">
              <a href="/pricing" rel="noopener" target="_blank">
                Canny’s pricing
              </a>{' '}
              is straightforward and offers more value. You can sign up with one of our self-serve
              plans right away — no need to chat with our&nbsp;team.
            </p>
            <div className="pricingContainer">
              <div className="pricingPlans">
                <div className="pricingPlan">
                  <p className="planName">Free</p>
                  <div className="price">
                    <p className="number">$0</p>
                    <p className="perMonth">/ mo</p>
                  </div>
                </div>
                <div className="pricingPlan">
                  <p className="planName">Core, starts at</p>
                  <div className="price">
                    <p className="number">$19</p>
                    <p className="perMonth">/ mo</p>
                  </div>
                </div>
                <div className="pricingPlan">
                  <p className="planName">Pro, starts at</p>
                  <div className="price">
                    <p className="number">$79</p>
                    <p className="perMonth">/ mo</p>
                  </div>
                </div>
              </div>
              <div className="morePricing">
                <p className="morePricingText">
                  More details and custom pricing options&nbsp;available
                </p>
                <Link className="cta" to="/pricing">
                  View&nbsp;pricing
                </Link>
              </div>
            </div>

            <p className="text">
              Need a demo, or pricing that’s tailored to your specific needs? You got it. We’re
              standing by—you’ll be able to{' '}
              <Link to="/request-demo">hop on a call with our team</Link> and discuss
              your&nbsp;options.
            </p>
          </div>
        </div>
        <div className="section" id="customers">
          <h2 className="sectionHeader">Canny customers and use&nbsp;cases</h2>
          <div className="sectionContent">
            <p className="text">
              Pendo Feedback can work if you're a huge company already using Pendo for product
              adoption analytics. You'll have less functionality, but would only need one&nbsp;tool.
            </p>
            <p className="text">
              If you're looking to maximize the value of feedback with best-in-class tools, Pendo
              Feedback can't&nbsp;deliver.
            </p>
            <p className="text">
              Canny is a flexible, customizable solution. We have customers all the way from
              2-person startups to billion dollar companies like{' '}
              <a href="/case-studies" rel="noopener" target="_blank">
                CircleCI and&nbsp;Mercury.
              </a>
            </p>
            <div className="quoteContainer">
              <p className="quote">
                <p className="text">
                  “We already use so many different tools, so adopting a new one can come with its
                  challenges. But Canny is so easy to use and so straightforward that switching to
                  it felt&nbsp;natural.”
                </p>
              </p>
              <div className="credit">
                <p className="text">
                  <div className="credit">
                    <p className="text">
                      <p className="name">Ida Ström</p>
                      <p className="title">
                        Senior Product Designer at{' '}
                        <a href="/case-studies/mercury" rel="noopener" target="_blank">
                          Mercury
                        </a>
                      </p>
                    </p>
                  </div>
                </p>
              </div>
            </div>
            <p className="text">
              And, Canny isn’t just for collecting feedback from customers.{' '}
              <Link to="/use-cases/internal-feedback">Internal teams</Link> also{' '}
              <Link to="/features/product-roadmap">
                use it to link user feedback to their product roadmap
              </Link>{' '}
              and <Link to="/features/product-changelog">announce feature&nbsp;updates</Link>.
            </p>
          </div>
        </div>
        <div className="section importData" id="migration">
          <h2 className="sectionHeader">Switching to Canny from&nbsp;Pendo</h2>
          <div className="sectionContent">
            <p className="text">Currently a Pendo Feedback customer?</p>
            <p className="text">
              Switching is easy and free. We can migrate all of your data from Pendo to Canny within
              48 hours. <Link to="/request-demo">Reach out to us</Link> to get&nbsp;started.
            </p>
          </div>
        </div>
        <LandingLogos />
        <LandingFooterCTA style="solid" />
      </div>
    </div>
  );
};

export default hot(LandingPendo);
